import { Helmet } from "react-helmet-async";
import { COMPANY } from "@/data/company";

// Centralised SEO head: title, meta description, canonical, Open Graph, JSON-LD.
export default function Seo({
  title,
  description,
  path = "/",
  image,
  type = "website",
  noindex = false,
  jsonLd = [],
}) {
  const url = COMPANY.siteUrl + path;
  const ogImage = image || COMPANY.siteUrl + COMPANY.logo;
  const schemas = Array.isArray(jsonLd) ? jsonLd : [jsonLd];

  return (
    <Helmet>
      <html lang="fr" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={COMPANY.name} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="fr_FR" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {schemas.map((s, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}

export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: COMPANY.name,
  image: COMPANY.siteUrl + COMPANY.logo,
  "@id": COMPANY.siteUrl,
  url: COMPANY.siteUrl,
  telephone: "+33610443864",
  email: COMPANY.email,
  address: {
    "@type": "PostalAddress",
    streetAddress: COMPANY.address.street,
    postalCode: COMPANY.address.postalCode,
    addressLocality: COMPANY.address.city,
    addressRegion: COMPANY.address.region,
    addressCountry: "FR",
  },
  areaServed: [
    "Ain", "Pays de Gex", "Haute-Savoie", "Lyon", "Auvergne-Rhône-Alpes",
    "Genève", "Nyon", "Lausanne", "Vaud", "Suisse romande",
  ],
  description:
    "Conception, fabrication et pose d'enseignes lumineuses, lettres relief, signalétique et communication visuelle. France & Suisse.",
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.label,
    item: COMPANY.siteUrl + it.to,
  })),
});

export const faqSchema = (faq) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faq.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const serviceSchema = (name, description, path) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  serviceType: name,
  provider: { "@type": "LocalBusiness", name: COMPANY.name, url: COMPANY.siteUrl },
  areaServed: ["France", "Suisse"],
  name,
  description,
  url: COMPANY.siteUrl + path,
});

export const articleSchema = (a, path) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: a.title,
  description: a.metaDescription,
  image: a.image,
  datePublished: a.date,
  author: { "@type": "Organization", name: COMPANY.name },
  publisher: {
    "@type": "Organization",
    name: COMPANY.name,
    logo: { "@type": "ImageObject", url: COMPANY.siteUrl + COMPANY.logo },
  },
  mainEntityOfPage: COMPANY.siteUrl + path,
});
